import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Router from "next/router";
import differenceInDays from "date-fns/differenceInDays";
import { FluentResource } from "@fluent/bundle";
import { LocalizationProvider, Localized } from "@fluent/react";
import { useElements, useStripe } from "@stripe/react-stripe-js";

import { getQueryParam } from "@kikoff/client-utils/src/dom";
import KButton from "@kikoff/components/src/v1/buttons/KButton";
import Switch from "@kikoff/components/src/v1/inputs/Switch";
import Strike from "@kikoff/components/src/v1/text/Strike";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import useId from "@kikoff/hooks/src/useId";
import { google, web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { msIn } from "@kikoff/utils/src/date";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";
import { combineClasses, format } from "@kikoff/utils/src/string";

import PageAlign from "@component/dashboard/offer/page_align";
import ViewTemplate from "@component/dashboard/offer/view_template";
import { PaymentSchedule } from "@component/dashboard/store/payment_plan";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import { KCheckbox } from "@component/inputs/checkbox";
import Line from "@component/line";
import { AutopayTermsLink } from "@component/modals/autopay";
import StreamlinedPaymentPromoCard from "@component/onboarding/promo";
import StreamlinedHeader from "@component/onboarding/streamlined_header";
import StreamlinedPaymentMethod from "@component/payment/streamlined";
import ProductEntry from "@component/shopping/product_entry";
import {
  LAST_MONTH_FREE_PROMO,
  LAST_MONTH_FREE_PROMO_PREMIUM,
} from "@constant/promos";
import {
  fetchCreditLineAccount,
  selectCreditAccount,
  setupCreditLineRepayment,
} from "@feature/credit_line";
import { triggerPostAddPaymentMethodHook } from "@feature/funds";
import { initOnboarding } from "@feature/onboarding";
import { dismiss, resetState, selectFeatureFlag } from "@feature/page";
import { updateBag } from "@feature/shopping";
import { Plan } from "@feature/subscription";
import {
  selectCheckoutPromos,
  selectFirstMonthCheckoutPromo,
} from "@feature/user";
import {
  getKikoffCreditAccountMentalModelName,
  useCreditServiceGenericMentalModelName,
} from "@src/constants";
import analytics, { track } from "@util/analytics";
import { newReactLocalization } from "@util/l10n";

import { CreditAccountAgreementLink } from "../agreement";
import cashappLogo from "../assets/images/cashapp-logo.svg";

import styles from "./styles.module.scss";

const RESOURCES = {
  en: new FluentResource(`store-payment-title = Set up repayment for your tradeline.
schedule-payment-default-button-text = First Payment Date
store-payment-agreement = I understand I will be opening a {$kikoffCreditAccount} and will be responsible for monthly payments. Credit service auto-renews yearly. Cancel anytime.
store-payment-autopay-enabled-well-text = Automatically make payments each month. Your first payment will be processed today. Your next payment will be on {$formattedNextAutopayDate}.
autopay = Autopay
store-payment-schedule-first-payment = Schedule first payment
store-payment-submit-payment-button-default-text = Submit payment
store-payment-schedule-payment-button-default-text = Schedule payment
streamlined-agreement-terms = I’ve read and agree to the terms of the
and-the = and the
store-payment-payment-due = Payment due
store-payment-payment-due-first = First payment due
schedule-payment-instruction = Your credit building will start after your first payment is complete. Future payments are due by the {$formattedDayDueEachMonth} of each month. Enable autopay to avoid missing a payment.
first-month-discounted-promo-disclaimer = *Offer applies to the first {$offerLength} month(s) of purchase of a 12-month {$kikoffCreditService} plan. Each subsequent month is billed at {$monthlyAmount}/month. Terms of offer are subject to change.
last-month-discounted-promo-disclaimer = *Offer applies to the last month of purchase of a 12-month {$kikoffCreditService} plan. The first 11 months are billed at {$monthlyAmount}/month. Terms of offer are subject to change.
payment-details = Payment details
repayment-schedule-title = Here's your repayment schedule
persona-disclaimer = We will report your account to the credit bureaus upon approval of Persona identity verification
`),
  es: new FluentResource(`store-payment-title = Configura tu pago para finalizar.
schedule-payment-default-button-text = Primera fecha de pago
store-payment-agreement = Entiendo que abriré una {$kikoffCreditAccount} y seré responsable de los pagos mensuales. La suscripción se renueva automáticamente cada año. Cancele en cualquier momento.
store-payment-autopay-enabled-well-text = Realizar pagos automáticamente cada mes. Su primer pago será procesado hoy. Tu próximo pago será el {$formattedNextAutopayDate}.
autopay = Pago automático
store-payment-schedule-first-payment = Programar primer pago
store-payment-submit-payment-button-default-texttore-payment-autopay-enabled-well-text = Realizar pagos automáticamente cada mes. Su primer pago será procesado hoy. Tu próximo pago será el {$formattedNextAutopayDate}.
autopay = Pago automático
store-payment-schedule-first-payment = Programar primer pago
store-payment-submit-payment-button-default-text = Enviar pago
store-payment-schedule-payment-button-default-text = Pago programado
streamlined-agreement-terms = He leído y estoy de acuerdo con los términos del
and-the = y el
store-payment-payment-due = Pago adeudado
store-payment-payment-due = Primer pago adeudado
schedule-payment-instruction = Su creación de crédito comenzará después de que se complete su primer pago. Los pagos futuros vencen el {$formattedDayDueEachMonth} de cada mes. Habilite el pago automático para evitar perder un pago.
first-month-discounted-promo-disclaimer = *La oferta se aplica al primer {$offerLength} mes de la compra de un plan de servicio de crédito Kikoff de 12 meses. Cada mes subsiguiente se factura a {$monthlyAmount}/mes. Los términos de la oferta están sujetos a cambios.
last-month-discounted-promo-disclaimer = *La oferta se aplica al último mes de la compra de un plan de Servicio de Crédito Kikoff de 12 meses. Los primeros 11 meses se facturan a {$monthlyAmount}/mes. Los términos de la oferta están sujetos a cambios.
payment-details = Detalles del pago
repayment-schedule-title = Aquí está su calendario de pagos
persona-disclaimer = Informaremos su cuenta a las agencias de crédito una vez que se apruebe la verificación de identidad de la Persona.
`),
};

export const l10n = newReactLocalization(RESOURCES);

export function PersonaPassableDisclaimer() {
  return (
    <div className={styles["disclaimer-text"]}>
      {l10n.getString("persona-disclaimer")}
    </div>
  );
}

function getDisclaimer(p: string) {
  switch (p) {
    case LAST_MONTH_FREE_PROMO:
    case LAST_MONTH_FREE_PROMO_PREMIUM:
      return "last-month-discounted-promo-disclaimer";
    default:
      return "first-month-discounted-promo-disclaimer";
  }
}

interface PromoDisclaimerProps {
  plan: web.public_.SubscriptionPlan;
  creditLineAgreement: web.public_.ICreditLineAgreement;
}

export function PromoDisclaimer({
  plan,
  creditLineAgreement,
}: PromoDisclaimerProps) {
  const promoByTier = useSelector(selectCheckoutPromos());
  const tier = Plan.byProto[plan];
  const promo = promoByTier[tier];
  const creditServiceGenericMentalModelName = useCreditServiceGenericMentalModelName();

  if (!promo) {
    return null;
  }

  const disclaimerText = getDisclaimer(promo.name);

  return (
    <div className={styles["disclaimer-text"]}>
      {l10n.getString(disclaimerText, {
        offerLength: promo.monthsCredited,
        monthlyAmount: format.money(
          creditLineAgreement.minimumPaymentAmountCents
        ),
        kikoffCreditService: creditServiceGenericMentalModelName,
      })}
    </div>
  );
}

export function AutopaySwitch({ autopayEnabled, setAutopayEnabled }) {
  return (
    <Switch
      checked={autopayEnabled}
      data-cy="payment-autopay-toggle"
      onChange={() => {
        const enabled = !autopayEnabled;
        track.tap("CA - Payment - Autopay Toggle Button", {
          enabled,
        });

        setAutopayEnabled(enabled);
      }}
    />
  );
}

function formatScheduledPaymentDate(date: google.protobuf.ITimestamp) {
  return (
    format.date(date, "Mmm") +
    " " +
    format.ordinality(Number(format.date(date, "dd")))
  );
}

function SelectorOption({ value, optionFormatter, onClick, setDropdownOpen }) {
  const formattedOption = optionFormatter(value);

  return (
    <div
      className={styles["dropdown-option"]}
      data-cy="dropdown-option"
      data-value={`${value}`}
      onClick={() => {
        onClick(value);
        setDropdownOpen(false);
        track.tap("CA - Payment - Select Scheduled Payment Date", {
          formattedOption,
          value,
        });
      }}
    >
      {formattedOption}
    </div>
  );
}

function generateDateRange(lastDateInS: number) {
  const today = new Date();
  const days = differenceInDays(new Date(lastDateInS * 1000), today) + 2;
  return [...Array(days)].fill(today.valueOf()).map((day, i) => {
    return day + i * msIn.day;
  });
}

function updateDropdownWidth(selectorId: string, dropdownId: string) {
  const selectorEl = document.getElementById(selectorId);
  const dropdownEl = document.getElementById(dropdownId);
  if (dropdownEl && selectorEl) {
    dropdownEl.style.width = selectorEl.scrollWidth + "px";
  }
}

interface SchedulePaymentSelectorProps {
  firstPaymentDueDate: google.protobuf.ITimestamp;
  scheduledPaymentDate: google.protobuf.ITimestamp;
  setScheduledPaymentDate: Function;
}

function SchedulePaymentSelector({
  firstPaymentDueDate,
  scheduledPaymentDate,
  setScheduledPaymentDate,
}: SchedulePaymentSelectorProps) {
  const wrapperId = useId();
  const selectorId = useId();
  const dropdownId = useId();
  const dropdownWrapperEl = useRef<HTMLDivElement>(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  function closeDropdown() {
    setDropdownOpen(false);
  }

  function clickListener(e) {
    if (
      !dropdownWrapperEl.current ||
      dropdownWrapperEl.current === e.target ||
      dropdownWrapperEl.current.contains(e.target)
    ) {
      return;
    }

    closeDropdown();
  }

  function addSelectorEventListeners() {
    window.addEventListener("resize", closeDropdown);
    window.addEventListener("click", clickListener);
  }

  function removeSelectorEventListeners() {
    window.removeEventListener("resize", closeDropdown);
    window.removeEventListener("click", clickListener);
  }

  useEffect(() => {
    addSelectorEventListeners();

    return () => {
      removeSelectorEventListeners();
    };
  }, []);

  const defaultButtonText = l10n.getString(
    "schedule-payment-default-button-text"
  );
  const buttonText = scheduledPaymentDate
    ? formatScheduledPaymentDate(scheduledPaymentDate)
    : defaultButtonText;
  const isDefaultValue = scheduledPaymentDate === null;

  const buttonTextClasses = combineClasses(
    styles["selector-button-text"],
    isDefaultValue && styles["default-button-text"]
  );
  const dropdownClasses = combineClasses(
    styles["selector-dropdown"],
    dropdownOpen && styles["dropdown-open"]
  );
  const selectorClasses = combineClasses(
    styles["schedule-payment-selector"],
    dropdownOpen && styles["selector-open"]
  );

  const dateOptions = generateDateRange(firstPaymentDueDate.seconds);

  return (
    <div id={wrapperId} ref={dropdownWrapperEl}>
      <div
        className={selectorClasses}
        data-cy="schedule-payment-selector"
        id={selectorId}
        onClick={() => {
          updateDropdownWidth(selectorId, dropdownId);
          setDropdownOpen(!dropdownOpen);
          track.tap("CA - Payment - Schedule Payment Select Button", {
            opened: !dropdownOpen,
          });
        }}
      >
        <div className={styles["selector-text-wrapper"]}>
          {!isDefaultValue && (
            <div className={styles["selector-label"]}>{defaultButtonText}</div>
          )}
          <div className={buttonTextClasses}>{buttonText}</div>
        </div>
        
      </div>
      <div className={dropdownClasses} id={dropdownId}>
        {dateOptions.map((date) => {
          return (
            <SelectorOption
              key={date}
              onClick={setScheduledPaymentDate}
              optionFormatter={formatScheduledPaymentDate}
              setDropdownOpen={setDropdownOpen}
              value={date}
            />
          );
        })}
      </div>
    </div>
  );
}

interface StreamlinedAutopaySectionProps {
  autopayEnabled: boolean;
  scheduledPaymentDate: google.protobuf.ITimestamp;
  setAutopayEnabled: Function;
  setScheduledPaymentDate: Function;
  creditLine?: web.public_.ICreditLineAccount;
}

export function StreamlinedAutopaySection({
  autopayEnabled,
  scheduledPaymentDate,
  setAutopayEnabled,
  setScheduledPaymentDate,
  creditLine,
}: StreamlinedAutopaySectionProps) {
  const checkoutPreview = useSelector(
    (state) => state.shopping.checkoutPreview
  );
  // AutoPay will process your payment on the due date for each installment.
  const firstPaymentDueDate =
    checkoutPreview?.paymentSchedule.payments[0].dueDate ||
    creditLine?.paymentSchedule.payments[0].dueDate;

  const formattedDayDueEachMonth = format.ordinality(
    Number(format.date(firstPaymentDueDate, "dd"))
  );

  const nextAutopayableDate =
    checkoutPreview?.paymentSchedule.payments[1].autopayableDate ||
    creditLine?.paymentSchedule.payments[1].dueDate;

  // NOTE: autopay date is not due date. Autopay date is after statement is cut.
  const formattedNextAutopayDate = formatScheduledPaymentDate(
    nextAutopayableDate
  );

  const autopayEnabledWellText = l10n.getString(
    "store-payment-autopay-enabled-well-text",
    { formattedNextAutopayDate }
  );

  return (
    <div className={styles["autopay-section"]}>
      <header className={styles["autopay-header"]}>
        <Localized id="autopay">Autopay</Localized>
        <AutopaySwitch
          autopayEnabled={autopayEnabled}
          setAutopayEnabled={setAutopayEnabled}
        />
      </header>
      <>
        {autopayEnabled ? (
          <p className={styles["short-p"]}>{autopayEnabledWellText}</p>
        ) : (
          <>
            <p className={styles["schedule-first-payment-header"]}>
              <Localized id="store-payment-schedule-first-payment">
                Schedule first payment
              </Localized>
            </p>
            <p className={styles["short-p"]}>
              <Localized
                id="schedule-payment-instruction"
                vars={{ formattedDayDueEachMonth }}
              />
            </p>
            <SchedulePaymentSelector
              scheduledPaymentDate={scheduledPaymentDate}
              setScheduledPaymentDate={setScheduledPaymentDate}
              firstPaymentDueDate={firstPaymentDueDate}
            />
          </>
        )}
      </>
    </div>
  );
}

export function StreamlinedAgreementSection({
  agree,
  setAgree,
  autopayEnabled,
}) {
  const id = useId();

  return (
    <label className={styles["agreement-checkbox"]} htmlFor={id}>
      <KCheckbox
        checked={agree}
        id={id}
        onChange={(e) => {
          setAgree(e.currentTarget.checked);
        }}
      >
        <p className={styles["checkbox-terms"]}>
          {autopayEnabled ? (
            <>
              <Localized id="streamlined-agreement-terms">
                I’ve read and agree to the terms of the
              </Localized>{" "}
              <CreditAccountAgreementLink /> <Localized id="and-the" />{" "}
              <AutopayTermsLink />.
            </>
          ) : (
            <>
              <Localized id="streamlined-agreement-terms">
                I’ve read and agree to the terms of the
              </Localized>{" "}
              <CreditAccountAgreementLink />.
            </>
          )}
        </p>
      </KCheckbox>
    </label>
  );
}

interface DiscountedPaymentProps {
  product: web.public_.IProduct;
  caAlreadyOpened?: boolean;
}

export function DiscountedPayment({
  product,
  caAlreadyOpened = false,
}: DiscountedPaymentProps) {
  if (!product) {
    return null;
  }

  const originalPayment = product.monthlyPaymentCents;

  const promo = useSelector(selectFirstMonthCheckoutPromo(caAlreadyOpened));
  if (!promo) {
    return <div>{format.money(originalPayment)}</div>;
  }

  const discount = promo.monthlyCreditAmountCents;
  const newPayment = originalPayment - discount;

  return (
    <div className="column-center">
      <div className="text:+">{format.money(newPayment)}</div>
      <Strike className="color:moderate-weak">
        {format.money(originalPayment)}
      </Strike>
    </div>
  );
}

function StreamlinedProductEntry() {
  const preview = useSelector((state) => state.shopping.checkoutPreview);
  const product = useSelector((state) => {
    return state.shopping.products.find(({ token }) => {
      return preview.order.orderItems[0].product.token === token;
    });
  });
  const emphasizeLending = useSelector((state) => state.page.emphasizeLending);

  const promo = useSelector(selectFirstMonthCheckoutPromo());

  const paymentDueLocaleKey = promo
    ? "store-payment-payment-due-first"
    : "store-payment-payment-due";

  return (
    <div className={styles["streamlined-product-entry"]}>
      <div>
        <ProductEntry product={product} contentOnly />
      </div>
      <div className={styles["payment-details"]}>
        <div className="text:large+">
          <Localized
            id={
              emphasizeLending ? "repayment-schedule-title" : "payment-details"
            }
          />
        </div>
        {promo && (
          <StreamlinedPaymentPromoCard variant="success" className="mt-2" />
        )}
        {emphasizeLending && (
          <>
            <br />
            <PaymentSchedule paymentSchedule={preview.paymentSchedule} />
          </>
        )}
        <div className={styles["due-now-row"]}>
          <div className="expand">
            <Localized id={paymentDueLocaleKey} />
          </div>
          <DiscountedPayment product={product} />
        </div>
      </div>
    </div>
  );
}

interface StreamlinedPaymentViewProps extends PageRegistration {}

function StreamlinedPaymentView({
  configFlow,
  page,
}: StreamlinedPaymentViewProps) {
  const initialSubmitButtonText = l10n.getString(
    "store-payment-submit-payment-button-default-text"
  );
  const initialScheduleButtonText = l10n.getString(
    "store-payment-schedule-payment-button-default-text"
  );
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const error = useErrorConsumer();

  const creditLine = useSelector(selectCreditAccount());
  const cashappEnabled = useSelector(selectFeatureFlag("cashapp_stripe_pay"));

  const blockDashboard = useSelector(
    selectFeatureFlag("block_dashboard_unless_ca")
  );

  const checkoutPreview = useSelector(
    (state) => state.shopping.checkoutPreview
  );
  const plan = checkoutPreview?.order.orderItems[0].product.plan;

  const [paymentMethodToken, setPaymentMethodToken] = useState<string>();
  const [validCardEntry, setValidCardEntry] = useState(false);
  const [autopayEnabled, setAutopayEnabled] = useState(true);
  const [agree, setAgree] = useState(false);
  const [
    postOnboardingStatus,
    setPostOnboardingStatus,
  ] = useState<web.public_.CompletionStatusResponse.CompletionStatus>();
  const [shouldContinueCashapp, setShouldContinueCashapp] = useState(false);

  const [submitButtonText, setSubmitButtonText] = useState(
    initialSubmitButtonText
  );

  const [cashappSubmitButtonText, setCashappSubmitButtonText] = useState(
    "Pay with Cash App"
  );

  const [pending, setPending] = useState(false);

  function resetSubmitButton() {
    setSubmitButtonText(
      autopayEnabled ? initialSubmitButtonText : initialScheduleButtonText
    );
    setCashappSubmitButtonText("Pay with Cash App");
    setPending(false);
  }

  useEffect(() => {
    resetSubmitButton();
  }, [autopayEnabled]);

  const [scheduledPaymentDate, setScheduledPaymentDate] = useState(null);

  // note that bag items and autorenew added to bag in StreamlinedEducationView.
  useEffect(() => {
    if (scheduledPaymentDate) {
      dispatch(
        updateBag({
          firstPaymentDate: google.protobuf.Timestamp.create({
            seconds: scheduledPaymentDate / 1000,
          }),
        })
      );
      window.localStorage.setItem("first-payment-date", scheduledPaymentDate);
    }
  }, [scheduledPaymentDate]);

  const paymentButtonsDisabled =
    !agree || (!autopayEnabled && scheduledPaymentDate === null) || pending;
  const submitButtonDisabled =
    !(validCardEntry || paymentMethodToken) || paymentButtonsDisabled;

  configFlow.none();

  useEffect(() => {
    track.impression("CA - Payment - Screen");
    analytics.only("Criteo", "Google Tag Manager").convert("Pre Checkout");
  }, []);

  useEffect(() => {
    webRPC.PostBoarding.completionStatus({}).then(
      handleProtoStatus({
        SUCCESS(data) {
          if (
            data.completionStatus ===
            web.public_.CompletionStatusResponse.CompletionStatus.PERSONA_FAILED
          ) {
            dispatch(initOnboarding());
          }
          setPostOnboardingStatus(data.completionStatus);
        },
        _DEFAULT: handleFailedStatus("Failed to get post boarding status"),
      })
    );
  }, []);

  useEffect(() => {
    setAutopayEnabled(!window.localStorage.getItem("first-payment-date"));

    if (getQueryParam.presence("setup_intent")) {
      setShouldContinueCashapp(true);
    }
  }, []);

  useEffect(() => {
    if (shouldContinueCashapp) {
      finishCashAppPayment();
    }
  }, [shouldContinueCashapp]);

  const finishCashAppPayment = async () => {
    const setupIntentId = getQueryParam("setup_intent");
    const firstPaymentDate = window.localStorage.getItem("first-payment-date");
    if (firstPaymentDate) {
      dispatch(
        updateBag({
          firstPaymentDate: google.protobuf.Timestamp.create({
            seconds: parseInt(firstPaymentDate, 10) / 1000,
          }),
        })
      );
    }

    const methodToken = await dispatch(
      triggerPostAddPaymentMethodHook({
        setupIntentId,
        onUpdate: setCashappSubmitButtonText,
        noRefresh: true,
      })
    ).catch((err) => {
      err.name = "PaymentMethodError";
      throw err;
    });

    setupCreditLine({
      cashapp: true,
      methodToken,
      setButtonText: setCashappSubmitButtonText,
    });
  };

  const payCreditLine = autopayEnabled;
  const showPassableDisclaimer =
    postOnboardingStatus ===
    web.public_.CompletionStatusResponse.CompletionStatus.PERSONA_PENDING;

  const submitPaymentButton = async ({ cashapp = false }) => {
    const setButtonText = cashapp
      ? setCashappSubmitButtonText
      : setSubmitButtonText;

    track.tap("CA - Payment - Submit Payment Button", { cashapp });

    setPending(true);

    setButtonText("Submitting payment...");
    if (autopayEnabled) dispatch(dismiss("AUTOPAY_TERMS"));

    setupCreditLine({
      cashapp,
      methodToken: cashapp ? null : paymentMethodToken,
      setButtonText,
    });
  };

  const setupCreditLine = ({
    cashapp,
    methodToken,
    setButtonText,
  }: {
    cashapp: boolean;
    methodToken: string;
    setButtonText: (text: string) => void;
  }) => {
    dispatch(
      setupCreditLineRepayment({
        autopayEnabled,
        elements,
        onUpdate(text) {
          setButtonText(text);
        },
        openCreditLine: !creditLine || !!creditLine.closedAt,
        payCreditLine,
        paymentMethodToken: methodToken,
        stripe,
        cashapp,
      })
    )
      .then(() => {
        track("CA - Payment - Payment Success", { cashapp });

        if (creditLine?.closedAt) {
          // If user has an existing closed creditLine, we are restarting the CA flow
          track("CA - Payment - Restart Payment - Success");
        }
        window.localStorage.removeItem("first-payment-date");

        dispatch(resetState("creditDisputes"));

        page.controller.next();
      })
      .catch(async (err) => {
        if (!err.ignore) error.throw(err.message);

        if (err.name === "CreditLinePaymentError" && blockDashboard) {
          await dispatch(fetchCreditLineAccount());

          Router.replace("/dashboard/store/make-first-payment");
        }

        resetSubmitButton();
      });
  };

  return (
    <PageAlign
      className={styles["page-align-no-padding"]}
      scroll
      overscroll={false}
    >
      <LocalizationProvider l10n={l10n}>
        <ViewTemplate className={styles["flat-payment-view"]}>
          <StreamlinedHeader
            displayBackButton
            displayCloseButton
            page={page}
            onBack={() => {
              page.controller.history.back();
            }}
          />
          <h3
            className={`${styles["streamlined-payment-title"]} text:heading-3`}
            data-cy="streamlined-payment-title"
          >
            <Localized id="store-payment-title" />
          </h3>
          <div className={styles["flat-wrapper"]}>
            <StreamlinedProductEntry />
            <Line />
            {!cashappEnabled && (
              <StreamlinedPaymentMethod
                setPaymentMethodToken={setPaymentMethodToken}
                setValidCardEntry={setValidCardEntry}
              />
            )}
            <StreamlinedAutopaySection
              autopayEnabled={autopayEnabled}
              scheduledPaymentDate={scheduledPaymentDate}
              setAutopayEnabled={setAutopayEnabled}
              setScheduledPaymentDate={setScheduledPaymentDate}
            />
            <StreamlinedAgreementSection
              agree={agree}
              autopayEnabled={autopayEnabled}
              setAgree={setAgree}
            />

            {cashappEnabled && (
              <div className={paymentButtonsDisabled ? styles.disabled : null}>
                <StreamlinedPaymentMethod
                  setPaymentMethodToken={setPaymentMethodToken}
                  setValidCardEntry={setValidCardEntry}
                />
              </div>
            )}
            <div className={styles["button-wrapper"]}>
              <KButton
                variant="black"
                size="full-width"
                state={submitButtonDisabled || pending ? "disabled" : "active"}
                data-cy="submit-payment-button"
                className={`${styles["submit-payment-button"]} mt-3 text-center`}
                onClick={() => {
                  submitPaymentButton({ cashapp: false });
                }}
              >
                {submitButtonText}
              </KButton>
            </div>

            {cashappEnabled && (
              <div>
                <div className={styles["cashapp-divider"]}>
                  <div className={styles["line"]} />
                  OR
                  <div className={styles["line"]} />
                </div>

                <div className={styles["button-wrapper"]}>
                  <KButton
                    variant="black"
                    size="full-width"
                    state={paymentButtonsDisabled ? "disabled" : "active"}
                    className={`${styles["submit-payment-button"]} text-center `}
                    onClick={() => {
                      submitPaymentButton({ cashapp: true });
                    }}
                  >
                    <div className={styles["cashapp-submit-button"]}>
                      <img src={cashappLogo.src} />
                      {cashappSubmitButtonText}
                    </div>
                  </KButton>
                </div>
              </div>
            )}

            <div className={styles["disclaimer-text"]}>
              <Localized
                id="store-payment-agreement"
                vars={{
                  kikoffCreditAccount: getKikoffCreditAccountMentalModelName(),
                }}
              />
            </div>
            <PromoDisclaimer
              plan={plan}
              creditLineAgreement={checkoutPreview?.creditLineAgreement}
            />
            {showPassableDisclaimer && <PersonaPassableDisclaimer />}
          </div>
        </ViewTemplate>
      </LocalizationProvider>
    </PageAlign>
  );
}

export default StreamlinedPaymentView;
